import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'components/Shared/Toast'
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { fetchSearchs, removeSearch, findByConditions, createSearch, updateSearch as updateSearchApi, markMetadataInPdf, addDocument, analyzeDocument, deleteDocument, createSuggestion } from 'api/actions/search';
import apiClient, { fetcher } from 'api/client';
import { formatDistanceToNow } from 'utils'
import {
    Row,
    Col,
    Navbar,
    Button,
    Media,
    Container,
    Card,
    CardBody,
    CardTitle,
    CardSubtitle
} from 'reactstrap';
import clsx from 'clsx';
import SearchDetail from './SearchDetail';
import { MainHeader } from 'components/Shared/MainHeader';

function Metadata() {

    const queryClient = useQueryClient();
    const { t } = useTranslation(['translation', 'validations']);
    const [currentSearch, setCurrentSearch] = useState({})
    const { status, data, error } = useQuery('searchs', () => fetcher(fetchSearchs()))
    const { mutate: mutateAddSearch } = useMutation(({ name }) => fetcher(createSearch({ name, conditions: [], contractsFound: 0 })), {
        // onSettled: () => {
        //     queryClient.invalidateQueries('searchs')
        //   },
        onSuccess: (data) => {
            queryClient.setQueryData('searchs', old => ([...old, data]))
            toast.success(t('validations:messages.successfullyCreated', { context: 'female', entity: 'Metadata' }))
        }
    })
    const { mutate: mutateRemoveSearch } = useMutation((searchId) => fetcher(removeSearch(searchId)), {
        // Optimistically update the cache value on mutate, but store
        // the old value and return it so that it's accessible in case of
        // an error
        onMutate: (searchId) => {
            queryClient.cancelQueries('searchs')

            const previousValue = queryClient.getQueryData('searchs')

            queryClient.setQueryData('searchs', old => old.filter((s) => s._id !== searchId))

            return previousValue
        },
        onSuccess: () => {
            toast.success(t('validations:messages.successfullyDeleted', { context: 'female', entity: 'Busqueda' }))
        },
        // On failure, roll back to the previous value
        onError: (err, variables, previousValue) =>
            queryClient.setQueryData('searchs', previousValue),
        // After success or failure, refetch the todos query
        onSettled: () => {
            queryClient.invalidateQueries('searchs')
        },
    })

    const handleDoubleClick = async (searchId) => {
        const { data } = await apiClient(findByConditions({ searchId }));
        alert('Contract Ids Found: ', data);
    }

    const handleClick = (search) => {
        setCurrentSearch(search)
    }

    const handleDelete = async (search) => {

        mutateRemoveSearch(search._id);
        // if (data._id) setSearchs(searchs);
    }

    const handleCreateSearch = async () => {
        mutateAddSearch({ name: t('metadata.new_search') });
    }

    const handleCloseSearchDetail = () => {
        setCurrentSearch('')
    }

    const handleSearchUpdate = (searchUpdated) => {
        queryClient.setQueryData('searchs', old => old.map(item => {
            if (item._id === searchUpdated._id) {
                return {
                    ...item,
                    ...searchUpdated
                }
            }
            return item
        }))
    }

    return (
        <>
            <MainHeader title="metadata.search">
                <Button color="default" onClick={handleCreateSearch} title={t('workflow.new.new')}>
                    <i className="fas fa-plus fa-2x text-primary"></i>
                </Button>
            </MainHeader>

            <Container fluid>
                <Row>
                    {
                        status === 'loading' ?
                            <Col md="4"><Card className="shadow-sm rounded-lg"><CardBody className="p-5">{t('metadata.loading')}...</CardBody></Card></Col>
                            :
                            !data.length ?
                                <Col md="4"><Card className="shadow-sm rounded-lg"><CardBody className="p-5">{t('metadata.empty')}</CardBody></Card></Col>
                                :
                                data.map((search = {}) => (
                                    <SearchItem
                                        currentSearch={currentSearch}
                                        key={search._id}
                                        search={search}
                                        onClick={handleClick}
                                        onDelete={handleDelete}
                                    />
                                ))
                    }
                </Row>
            </Container>
            <SearchDetail
                isOpen={!!currentSearch._id}
                initialSearch={currentSearch}
                onClose={handleCloseSearchDetail}
                onSearchUpdate={handleSearchUpdate}
            />
        </>
    )
}

function SearchItem({ search = {}, onDelete, onClick, currentSearch }) {
    const { t } = useTranslation();

    const documentsAnalizedCount = useMemo(() => {
        return search?.documents?.reduce((total, doc) => {
            if (doc.isAnalized) {
                return total + 1;
            }
            return total
        }, 0)
    }, [search.documents])

    const handleDelete = (evt) => {
        evt.preventDefault();
        evt.stopPropagation();
        onDelete(search)
    }

    const handleClick = (evt) => {
        evt.preventDefault();
        evt.stopPropagation();
        onClick(search)
    }

    return (
        <Col md="4" className="mt-3 pointer" onClick={handleClick} >
            <Card className={clsx('shadow-sm rounded-lg', search._id === currentSearch._id && 'shadow')} >
                <CardBody>
                    <Button size="sm" color="default" className="float-right" onClick={handleDelete}>
                        <i className="fas fa-times-circle text-danger"></i>
                    </Button>
                    <Media className="mt-2">
                        <i className="fas fa-search fa-2x mr-3"></i>
                        <CardTitle tag="h5" className="text-break ml-3 text-truncate">{search.name}</CardTitle>
                    </Media>
                    <CardSubtitle className="text-muted mt-3">{t('common.by')} {search.creator?.givenName} {search.creator?.familyName} {formatDistanceToNow(search.createdAt)}</CardSubtitle>
                    <Media className="mt-3">
                        <Media body className="text-center">
                            <h4 className="workflow-item-counter font-weight-bold my-0">
                                {t('metadata.analysed_documents')}
                            </h4>
                            <h3 className="text-muted mt-3">
                                {documentsAnalizedCount ?? 0}
                            </h3>
                        </Media>
                    </Media>
                </CardBody>
            </Card>
        </Col>
    )
}

export default Metadata;


